import { useState, useEffect, useRef } from 'react';
import { Copy, Share2, MoreHorizontal } from 'lucide-react';
import { shareOnWhatsApp } from '../../lib/sharing';
import { useToast } from '../ui/ToastSystem';
import { useI18n } from '../../hooks/useI18n';

interface ReadingActionsProps {
  pageName: string;
  reading: string;
  signEmoji?: string;
  /** If provided, shows a "Leitura personalizada para [userName]" badge */
  userName?: string;
}

export function ReadingActions({ pageName, reading, signEmoji, userName }: ReadingActionsProps) {
  const { showToast } = useToast();
  const { t } = useI18n();
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropOpen) return;
    function handle(e: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setDropOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [dropOpen]);

  async function handleWhatsApp() {
    try {
      await shareOnWhatsApp({ pageName, reading, signEmoji });
    } catch {
      try {
        await navigator.clipboard.writeText(reading);
        showToast({ type: 'success', message: t('shared.shareCopied'), duration: 4000 });
      } catch {
        showToast({ type: 'error', message: t('shared.shareError') });
      }
    }
  }

  function handleTwitter() {
    const snippet = reading.length > 200 ? reading.slice(0, 197) + '…' : reading;
    const text = `${signEmoji ?? '✦'} ${pageName}, Professya\n\n${snippet}\n\nhttps://professya.com?utm_source=twitter&utm_medium=share`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
      '_blank',
      'noopener,noreferrer',
    );
    setDropOpen(false);
  }

  function handleFacebook() {
    const url = 'https://professya.com?utm_source=facebook&utm_medium=share';
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank',
      'noopener,noreferrer',
    );
    setDropOpen(false);
  }

  async function handleInstagram() {
    // Instagram has no web share API; copy reading text and prompt the user.
    try {
      await navigator.clipboard.writeText(
        `${signEmoji ?? '✦'} ${pageName}, Professya\n\n${reading.slice(0, 400)}\n\nprofessya.com`,
      );
      showToast({ type: 'success', message: 'Texto copiado! Abra o Instagram e cole na legenda.', duration: 5000 });
    } catch {
      showToast({ type: 'error', message: t('shared.shareError') });
    }
    setDropOpen(false);
  }

  async function handleCopyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast({ type: 'success', message: 'Link copiado!' });
    } catch {
      showToast({ type: 'error', message: t('shared.shareError') });
    }
  }

  async function handleNativeShare() {
    if (!navigator.share) return;
    try {
      await navigator.share({
        title: `${pageName}, Professya`,
        text: reading.length > 280 ? reading.slice(0, 277) + '…' : reading,
        url: window.location.href,
      });
    } catch {
      // User cancelled or not supported
    }
  }

  const hasNativeShare = typeof navigator !== 'undefined' && Boolean(navigator.share);

  const dropItemStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '0.5rem',
    width: '100%', padding: '0.45rem 0.75rem',
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 'var(--text-label)', color: 'var(--text-primary)',
    borderRadius: 'var(--radius-sm)', textAlign: 'left',
    transition: 'background 120ms',
  };

  return (
    <div style={{ paddingTop: '1rem' }}>
      {userName && (
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
          fontSize: 'var(--text-caption)', color: 'var(--petrol)',
          backgroundColor: 'rgba(245,200,66,0.08)',
          border: '1px solid rgba(245,200,66,0.2)',
          borderRadius: 'var(--radius-full)',
          padding: '0.2rem 0.65rem',
          marginBottom: '0.75rem',
        }}>
          ✦ Leitura personalizada para {userName}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* WhatsApp, primary action */}
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleWhatsApp}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <Share2 size={14} />
          {t('shared.shareWhatsApp')}
        </button>

        {/* Copy link */}
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleCopyLink}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
          aria-label="Copiar link"
        >
          <Copy size={14} />
          Copiar link
        </button>

        {/* More options dropdown (X, Facebook, Instagram) */}
        <div ref={dropRef} style={{ position: 'relative' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setDropOpen(o => !o)}
            aria-label="Mais opções de compartilhamento"
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
          >
            <MoreHorizontal size={14} />
            Mais
          </button>

          {dropOpen && (
            <div style={{
              position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, zIndex: 300,
              background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)', padding: '0.3rem',
              minWidth: '11rem', boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            }}>
              <button
                style={dropItemStyle}
                onClick={handleTwitter}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-input)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                𝕏&nbsp;&nbsp;X (Twitter)
              </button>
              <button
                style={dropItemStyle}
                onClick={handleFacebook}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-input)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <span style={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1 }}>f</span>
                Facebook
              </button>
              <button
                style={dropItemStyle}
                onClick={handleInstagram}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-input)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                📸&nbsp;Instagram
              </button>
            </div>
          )}
        </div>

        {/* Native share (mobile) */}
        {hasNativeShare && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleNativeShare}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            aria-label="Compartilhar"
          >
            <Share2 size={14} />
            Compartilhar
          </button>
        )}
      </div>
    </div>
  );
}
